import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ProductCard } from "../components/ProductCard";
import { useStore } from "../context/StoreContext";
import { apiClient } from "../lib/api";
import { PRIMARY_COLLECTIONS } from "../lib/collections";
import { applySeo } from "../lib/seo";

const categories = [
  { value: "all", label: "All Categories" },
  { value: "Oversized Collections", label: "Oversized Collections" },
  { value: "Acid Wash T-Shirts", label: "Acid Wash T-Shirts" },
];
const fits = ["all", "Regular", "Oversized"];
const colors = ["all", "Black", "Royal Blue", "White", "Brown"];
const sorts = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "price_low_high", label: "Price: Low to High" },
  { value: "price_high_low", label: "Price: High to Low" },
];

const collectionOrder = ["shiva", "veergatha", "madhav", "maryada", "anjaneya", "acid-wash-premium"];

const showcaseImageMap = {
  shiva: ["/assets/shiva-tile3.png", "/assets/whats-new-shiva.png"],
  veergatha: [
    "/assets/whats-new-veergatha.png",
  ],
  madhav: ["/assets/madhav-tile2.png", "/assets/whats-new-madhav.png"],
  maryada: ["/assets/maryada-tile4.png", "/assets/whats-new-maryada.png"],
  anjaneya: ["/assets/anjaneya-tile5.png", "/assets/whats-new-anjaneya.png"],
  "acid-wash-premium": ["/assets/whats-new-acid-wash-premium-no-text.png", "/assets/customize-tile6.png"],
};

const mapCategoryForApi = (categoryValue) => {
  if (categoryValue === "Oversized Collections") return "Oversized T-shirt";
  if (categoryValue === "Acid Wash T-Shirts") return "Acid Wash T-shirt";
  return categoryValue;
};

const isRenderableProduct = (product) => {
  if (!product) return false;
  const imageUrls = product.image_urls || [];
  const hasUploadedImage = imageUrls.some((url) => typeof url === "string" && url.trim().length > 0);
  return Boolean(product.artwork_front_url || product.artwork_back_url || hasUploadedImage);
};

export const ShopPage = () => {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "all";
  const initialCollectionSlug = searchParams.get("collection_slug") || "all";

  const [filters, setFilters] = useState({
    search: "",
    category: initialCategory,
    collection_slug: initialCollectionSlug,
    fit: "all",
    color: "all",
    sort: "featured",
    min_price: 0,
    max_price: 100000,
  });
  const [collections, setCollections] = useState(PRIMARY_COLLECTIONS);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showcaseIndex, setShowcaseIndex] = useState({
    shiva: 0,
    veergatha: 0,
    madhav: 0,
    maryada: 0,
    anjaneya: 0,
    "acid-wash-premium": 0,
  });
  const [missingShowcaseImages, setMissingShowcaseImages] = useState({});

  const { addToCart, wishlist, toggleWishlist } = useStore();

  const orderedCollections = useMemo(() => {
    const copied = [...collections];
    return copied.sort((a, b) => {
      const aIndex = collectionOrder.indexOf(a.slug);
      const bIndex = collectionOrder.indexOf(b.slug);
      const safeA = aIndex === -1 ? 999 : aIndex;
      const safeB = bIndex === -1 ? 999 : bIndex;
      return safeA - safeB;
    });
  }, [collections]);

  const showcaseCollections = useMemo(
    () => orderedCollections.filter((item) => collectionOrder.includes(item.slug)),
    [orderedCollections],
  );

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const queryPayload = {
        ...filters,
        category: mapCategoryForApi(filters.category),
      };
      const query = new URLSearchParams(queryPayload).toString();
      const [productsRes, collectionsRes] = await Promise.all([
        apiClient.get(`/products?${query}&limit=24`),
        apiClient.get("/collections"),
      ]);
      setProducts(productsRes.data.items || []);
      setCollections(collectionsRes.data.collections || PRIMARY_COLLECTIONS);
    } catch {
      toast.error("Unable to load shop products");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    applySeo({
      title: "Shop Premium Mythology Apparel | Eternal Order",
      description: "Browse premium artistic apparel by collection, size, fit, and story tags.",
    });
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (showcaseCollections.length === 0) return;
    const timer = setInterval(() => {
      setShowcaseIndex((prev) => {
        const next = { ...prev };
        showcaseCollections.forEach((collection) => {
          const imageSet = showcaseImageMap[collection.slug] || [collection.hero_image];
          const current = prev[collection.slug] || 0;
          next[collection.slug] = (current + 1) % imageSet.length;
        });
        return next;
      });
    }, 2800);
    return () => clearInterval(timer);
  }, [showcaseCollections]);

  const activeFilters = useMemo(
    () => [filters.category, filters.collection_slug, filters.fit, filters.color].filter((item) => item && item !== "all"),
    [filters],
  );
  const renderableProducts = useMemo(() => products.filter(isRenderableProduct), [products]);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[#bfa46f]">Shop Eternal Order</p>
        <h1 className="mt-2 text-3xl text-[#f4eee3] sm:text-4xl" data-testid="shop-page-heading">
          Curated wearable art
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-[#aa9d85]" data-testid="shop-page-description">
          Premium artistic apparel built for depth, form, and intention.
        </p>
      </div>

      <section className="mb-8" data-testid="shop-showcase-carousel-section">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {showcaseCollections.map((collection) => {
            const images = showcaseImageMap[collection.slug] || [collection.hero_image];
            const currentImage = images[(showcaseIndex[collection.slug] || 0) % images.length];
            return (
              <Link
                key={collection.slug}
                to={`/collections/${collection.slug}`}
                className="group overflow-hidden rounded-2xl border border-white/10 bg-[#0f1118]"
                data-testid={`shop-showcase-card-${collection.slug}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden" data-testid={`shop-showcase-image-wrapper-${collection.slug}`}>
                  {missingShowcaseImages[collection.slug] ? (
                    <div className="flex h-full w-full items-center justify-center bg-[#0a0d14]" data-testid={`shop-showcase-coming-soon-${collection.slug}`}>
                      <span className="rounded-full border border-[#C6A85A]/70 px-3 py-1 text-xs uppercase tracking-[0.14em] text-[#C6A85A]">
                        Coming Soon
                      </span>
                    </div>
                  ) : (
                    <img
                      src={currentImage}
                      alt={collection.name}
                      className="h-full w-full object-cover object-center transition duration-500 group-hover:scale-[1.03]"
                      onError={() => setMissingShowcaseImages((prev) => ({ ...prev, [collection.slug]: true }))}
                      data-testid={`shop-showcase-image-${collection.slug}`}
                    />
                  )}
                </div>
                <p className="px-3 py-2 text-center text-xs uppercase tracking-[0.15em] text-[#d7c8a9]" data-testid={`shop-showcase-title-${collection.slug}`}>
                  {collection.name}
                </p>
              </Link>
            );
          })}
        </div>
      </section>

      <div className="rounded-2xl border border-white/10 bg-[#111111] p-4 sm:p-5" data-testid="shop-filter-panel">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <input
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            placeholder="Search by name or story"
            className="rounded-full border border-white/15 bg-black/20 px-4 py-2.5 text-sm text-white outline-none focus:border-[#bfa46f]"
            data-testid="shop-search-input"
          />

          <select
            value={filters.category}
            onChange={(event) => setFilters((prev) => ({ ...prev, category: event.target.value }))}
            className="rounded-full border border-white/15 bg-black/20 px-4 py-2.5 text-sm text-white outline-none focus:border-[#bfa46f]"
            data-testid="shop-category-filter"
          >
            {categories.map((item) => (
              <option key={item.value} value={item.value} className="bg-[#111]">
                {item.label}
              </option>
            ))}
          </select>

          <select
            value={filters.collection_slug}
            onChange={(event) => setFilters((prev) => ({ ...prev, collection_slug: event.target.value }))}
            className="rounded-full border border-white/15 bg-black/20 px-4 py-2.5 text-sm text-white outline-none focus:border-[#bfa46f]"
            data-testid="shop-collection-filter"
          >
            <option value="all" className="bg-[#111]">
              All Collections
            </option>
            {orderedCollections.map((item) => (
              <option key={item.slug} value={item.slug} className="bg-[#111]">
                {item.name}
              </option>
            ))}
          </select>

          <select
            value={filters.fit}
            onChange={(event) => setFilters((prev) => ({ ...prev, fit: event.target.value }))}
            className="rounded-full border border-white/15 bg-black/20 px-4 py-2.5 text-sm text-white outline-none focus:border-[#bfa46f]"
            data-testid="shop-fit-filter"
          >
            {fits.map((item) => (
              <option key={item} value={item} className="bg-[#111]">
                {item === "all" ? "All Fits" : item}
              </option>
            ))}
          </select>

          <select
            value={filters.color}
            onChange={(event) => setFilters((prev) => ({ ...prev, color: event.target.value }))}
            className="rounded-full border border-white/15 bg-black/20 px-4 py-2.5 text-sm text-white outline-none focus:border-[#bfa46f]"
            data-testid="shop-color-filter"
          >
            {colors.map((item) => (
              <option key={item} value={item} className="bg-[#111]">
                {item === "all" ? "All Colors" : item}
              </option>
            ))}
          </select>

          <select
            value={filters.sort}
            onChange={(event) => setFilters((prev) => ({ ...prev, sort: event.target.value }))}
            className="rounded-full border border-white/15 bg-black/20 px-4 py-2.5 text-sm text-white outline-none focus:border-[#bfa46f]"
            data-testid="shop-sort-filter"
          >
            {sorts.map((item) => (
              <option key={item.value} value={item.value} className="bg-[#111]">
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="shop-filter-actions-row">
          <button
            type="button"
            onClick={() =>
              setFilters({
                search: "",
                category: "all",
                collection_slug: "all",
                fit: "all",
                color: "all",
                sort: "featured",
                min_price: 0,
                max_price: 100000,
              })
            }
            className="rounded-full border border-[#bfa46f]/45 px-4 py-1.5 text-xs text-[#e6d8bf] transition hover:border-[#bfa46f]"
            data-testid="shop-clear-filters-button"
          >
            Clear Filters
          </button>

          {activeFilters.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="shop-active-filter-list">
              {activeFilters.map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    const key =
                      filters.category === item
                        ? "category"
                        : filters.collection_slug === item
                          ? "collection_slug"
                          : filters.fit === item
                            ? "fit"
                            : "color";
                    setFilters((prev) => ({ ...prev, [key]: "all" }));
                  }}
                  className="rounded-full border border-[#bfa46f]/45 px-3 py-1 text-xs text-[#e6d8bf]"
                  data-testid={`shop-active-filter-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {item} ✕
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[#b3a68d]" data-testid="shop-loading-state">
          Loading products...
        </div>
      ) : renderableProducts.length === 1 ? (
        <div className="mx-auto mt-8 max-w-sm" data-testid="shop-single-product-wrap">
          <ProductCard
            key={renderableProducts[0].id}
            product={renderableProducts[0]}
            wishlisted={wishlist.includes(renderableProducts[0].id)}
            onWishlist={toggleWishlist}
            onAddToCart={addToCart}
          />
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="shop-product-grid">
          {renderableProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              wishlisted={wishlist.includes(product.id)}
              onWishlist={toggleWishlist}
              onAddToCart={addToCart}
            />
          ))}
        </div>
      )}

      {!loading && renderableProducts.length === 0 && (
        <div className="mt-10 rounded-2xl border border-white/10 bg-[#121212] p-8 text-center" data-testid="shop-empty-state">
          <p className="text-sm text-[#b9ab90]">No products match this filter combination yet.</p>
        </div>
      )}
    </div>
  );
};
